import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/authService';
import { AlertCircle, Loader2, ChevronRight, User, Lock, Eye, EyeOff, Facebook, Instagram } from 'lucide-react';
import PasswordResetForm from './PasswordResetForm';
import LogoJireh from '../assets/logo.jpg';

const Login = ({ setUser }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await login(username, password);
      setUser(data.user);
      navigate('/');
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gradient-to-r from-[#E3F2FD] via-[#E8F5E9] to-[#E3F2FD] relative overflow-hidden">
      {/* Elemento que sigue al mouse - solo visible en desktop */}
      <div 
        className="pointer-events-none fixed hidden lg:block w-24 h-24 rounded-full bg-gradient-to-r from-[#64B5F6]/20 to-[#81C784]/20 blur-xl"
        style={{
          left: mousePosition.x - 48,
          top: mousePosition.y - 48,
          transform: 'translate(-50%, -50%)',
          transition: 'all 0.3s ease-out'
        }}
      />
      <div 
        className="pointer-events-none fixed hidden lg:block w-12 h-12 rounded-full bg-gradient-to-r from-[#64B5F6]/30 to-[#81C784]/30 blur-md"
        style={{
          left: mousePosition.x - 24,
          top: mousePosition.y - 24,
          transform: 'translate(-50%, -50%)',
          transition: 'all 0.1s ease-out'
        }}
      />

      {/* Mobile Header */}
      <div className="lg:hidden w-full bg-white/30 backdrop-blur-sm py-4 px-4">
        <div className="flex items-center gap-4">
          <div className="relative w-12 h-12 bg-white rounded-full shadow-md flex items-center justify-center overflow-hidden">
            <img 
              src={LogoJireh}
              alt="Logo Centro Infantil Jireh"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#1E88E5]">
              Centro Infantil <span className="text-[#66BB6A]">Mi Jardin</span>
            </h1>
            <p className="text-xs text-[#546E7A]">
              Desarrollo integral infantil
            </p>
          </div>
        </div>
      </div>

      {/* Left side - Brand (solo visible en desktop) */}
      <div className="hidden lg:flex w-1/2 flex-col items-center justify-center relative overflow-hidden backdrop-blur-sm bg-white/30">
        {/* Decorative background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 1440 810" preserveAspectRatio="xMidYMid slice">
            <path fill="#81C784" fillOpacity="0.05" d="M0,288L48,272C96,256,192,224,288,197.3C384,171,480,149,576,165.3C672,181,768,235,864,261.3C960,288,1056,288,1152,266.7C1248,245,1344,203,1392,181.3L1440,160L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
          </svg>
        </div>

        <div className="text-center z-10 px-12">
          <div className="flex justify-center mb-8">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#64B5F6] to-[#81C784] rounded-full opacity-75 group-hover:opacity-100 blur transition duration-300"></div>
              <div className="relative w-52 h-52 bg-white rounded-full shadow-xl flex items-center justify-center overflow-hidden transform group-hover:scale-105 transition-transform duration-300">
                <img 
                  src={LogoJireh}
                  alt="Logo Centro Infantil Jireh"
                  className="w-48 h-48 object-contain p-2"
                />
              </div>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-[#1E88E5] tracking-tight mb-4">
            Centro Infantil <span className="text-[#66BB6A]">Mi Jardin</span>
          </h1>
          <p className="text-xl text-[#546E7A] max-w-md mx-auto">
            Cuidado, educación y amor para el desarrollo integral de los niños
          </p>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="w-full lg:w-1/2 flex-1 flex items-center justify-center backdrop-blur-sm bg-white/40 p-4 lg:p-12">
        <div className="w-full max-w-md">
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-[#E3F2FD] p-4 lg:p-8">
            <h2 className="text-xl lg:text-2xl font-semibold text-[#1E88E5] mb-6 text-center">
              Acceso al Sistema
            </h2>

            {/* Error Message */}
            {error && (
              <div className="mb-6 flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl border border-red-200 text-sm lg:text-base">
                <AlertCircle size={18} />
                <span className="font-medium">{error}</span>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-4 lg:space-y-6">
              <div>
                <label className="text-sm font-medium text-[#1E88E5] mb-2 flex items-center gap-2">
                  <User size={18} className="text-[#66BB6A]" />
                  Correo Electrónico
                </label>
                <input
                  type="text"
                  className="w-full px-3 lg:px-4 py-2 lg:py-3 bg-white/50 rounded-xl border border-[#B3E5FC] focus:border-[#81C784] focus:ring-4 focus:ring-[#81C784]/30 transition-all duration-300 outline-none text-gray-500 placeholder:text-[#90CAF9] text-sm lg:text-base"
                  placeholder="Ingresa tu correo"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#1E88E5] mb-2 flex items-center gap-2">
                  <Lock size={18} className="text-[#66BB6A]" />
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full px-3 lg:px-4 py-2 lg:py-3 bg-white/50 rounded-xl border border-[#B3E5FC] focus:border-[#81C784] focus:ring-4 focus:ring-[#81C784]/30 transition-all duration-300 outline-none text-gray-500 text-sm lg:text-base"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#66BB6A] hover:text-[#1E88E5] transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="text-right mb-4 lg:mb-6">
                <button 
                  type="button"
                  onClick={() => setShowResetForm(true)}
                  className="text-xs lg:text-sm text-[#1E88E5] hover:text-[#66BB6A] transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <button 
                type="submit"
                className="w-full py-2 lg:py-3 bg-gradient-to-r from-[#64B5F6] to-[#81C784] text-white rounded-xl hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2 font-semibold shadow-lg text-sm lg:text-base"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin w-4 h-4 lg:w-5 lg:h-5" />
                    Verificando...
                  </>
                ) : (
                  <>
                    Iniciar Sesión
                    <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" />
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="text-center mt-4 lg:mt-6">
            <div className="flex justify-center space-x-4 mb-2">
              <a 
                href="https://www.facebook.com/jirehcentro?locale=es_LA" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#3b5998] hover:text-[#2d4373] transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://www.instagram.com/mijardin_centro_/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#e1306c] hover:text-[#c13584] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
            <p className="text-xs lg:text-sm text-[#546E7A]">
              © {new Date().getFullYear()} Mi Jardín - Todos los derechos reservados
            </p>
          </div>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-md">
            <PasswordResetForm setShowResetForm={setShowResetForm} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;